import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { ReportType } from '@repo/shared/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/alert-dialog';
import { Button } from '@repo/ui/button';
import { cn } from '@repo/ui/lib/utils';
import { Building2, User } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { trpc } from '@/providers/trpc';

const META: Record<ReportType, { label: string; Icon: typeof User }> = {
  [ReportType.KYC]: {
    get label() {
      return t('reports.knowYourCustomerKyc');
    },
    Icon: User,
  },
  [ReportType.KYB]: {
    get label() {
      return t('reports.knowYourBusinessKyb');
    },
    Icon: Building2,
  },
};

type ReportTypeSidebarCardProps = {
  reportType: ReportType;
  className?: string;
  onChangeReportTypeConfirm: () => void;
};

export function ReportTypeSidebarCard({
  reportType,
  className,
  onChangeReportTypeConfirm,
}: ReportTypeSidebarCardProps) {
  useLocale();

  const { reportId = '' } = useParams();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { mutate: removeReport, isPending } = trpc.report.deleteReport.useMutation({
    onSuccess: () => {
      void setConfirmOpen(false);
      void onChangeReportTypeConfirm();
    },
    onError: () => {
      toast.error(t('reports.couldNotDiscardReport'), {
        get description() {
          return t('reports.pleaseTryAgain');
        },
      });
    },
  });

  const { Icon, label } = META[reportType];

  return (
    <>
      <div className={cn('mb-3 rounded-lg border border-border bg-card px-3 py-2.5', className)}>
        <p className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
          {t('reports.reportType')}{' '}
        </p>
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 font-semibold text-[10px] text-primary">
            1
          </span>
          <div className="flex min-w-0 items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-primary" />
            <span className="font-medium text-sm leading-tight">{label}</span>
          </div>
        </div>
        <p className="mb-3 text-muted-foreground text-xs leading-relaxed">
          {t('reports.youChoseThisVerificationPathForThisReportTo')}{' '}
        </p>
        <Button
          className="w-full"
          onClick={() => setConfirmOpen(true)}
          size="sm"
          type="button"
          variant="outline"
        >
          {t('reports.changeReportType')}{' '}
        </Button>
      </div>

      <AlertDialog onOpenChange={setConfirmOpen} open={confirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('reports.changeReportType2')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('reports.switchingTheReportTypeWillPermanentlyDiscardThisDraft')}{' '}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">{t('reports.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isPending}
              onClick={() => removeReport({ reportId })}
              type="button"
            >
              {isPending ? t('reports.discarding') : t('reports.discardAndStartOver')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
